import { useMemo } from 'react';
import { Template_TemplateType } from '@weave/schema-gen-ts/dist/schemas/reviews/landing-page-templates/v1/landing_page_template_service.pb';
import { useQueryClient } from 'react-query';
import {
  getListLandingPageTemplatesQueryKey,
  useDeleteLandingPageTemplateMutation,
  useListLandingPageTemplatesQuery,
} from '@frontend/api-landing-page-template';
import { useGetReviewsSettingsQuery } from '@frontend/api-review-sites';
import { ReviewsInvitationV1 } from '@frontend/api-reviews-invitation';
import { ReviewLandingPageTemplate, AffiliateReviewType, AffiliateReviewIds } from '../types';

type useGetReviewTemplatesProps = {
  selectedLocationIds: string[];
  handleListLandingPageTemplatesError?: () => void;
  handleListAffiliateReviewLinksError?: () => void;
};

type ResponseCallbacks = {
  handleErrorResponse?: () => void;
  handleSuccessResponse?: () => void;
};

type useGetReviewsInvitationTemplatesProps = ResponseCallbacks & {
  selectedLocationIds: string[];
  enabled: boolean;
};

/**
 * To get both landingPageTemplates and affiliate review (google/facebook) links.
 */
export const useGetReviewTemplates = ({
  selectedLocationIds,
  handleListLandingPageTemplatesError,
  handleListAffiliateReviewLinksError,
}: useGetReviewTemplatesProps) => {
  const {
    data: landingPageTemplates,
    isLoading: isListLandingPageTemplatesLoading,
    isError: isListLandingPageTemplatesError,
  } = useListLandingPageTemplatesQuery(
    {
      groupIds: selectedLocationIds,
    },
    {
      select: (data) => data.templates,
      onError: handleListLandingPageTemplatesError,
      cacheTime: 0,
      staleTime: 0,
      retry: 2,
    }
  );

  const {
    data: reviewSettings,
    isLoading: isReviewSettingsLoading,
    isError: isReviewSettingsError,
  } = useGetReviewsSettingsQuery(
    {
      groupIds: selectedLocationIds,
    },
    {
      onError: handleListAffiliateReviewLinksError,
      select: ({ locationReviewSettings }) => {
        const reviewSites = { facebookUrl: '', googleUrl: '' };
        if (!!locationReviewSettings && !!locationReviewSettings.length) {
          for (const setting of locationReviewSettings) {
            if (reviewSites.googleUrl && reviewSites.facebookUrl) break;

            if (!reviewSites.googleUrl && setting.googleUrl) {
              reviewSites.googleUrl = setting.googleUrl;
            }
            if (!reviewSites.facebookUrl && setting.facebookUrl) {
              reviewSites.facebookUrl = setting.facebookUrl;
            }
          }
        }
        return reviewSites;
      },
      cacheTime: 0,
      staleTime: 0,
      retry: 2,
    }
  );

  // Synchronize both landingPage templates and affiliate review links (google/facebook) to same schema.
  const landingPageReviewTemplates = useMemo<ReviewLandingPageTemplate[] | undefined>(() => {
    if (!landingPageTemplates) return undefined;
    return landingPageTemplates.map(({ previewLink, type, title, templateId, htmlConfig, renderedHtml }) => ({
      templateId: templateId || '',
      previewLink: previewLink || '',
      htmlConfig: htmlConfig || '',
      renderedHtml: renderedHtml || '',
      type: type ?? Template_TemplateType.CUSTOM,
      title: title || 'Landing Page - 00',
    }));
  }, [JSON.stringify(landingPageTemplates)]);

  const affiliateReviewTemplates = useMemo<ReviewLandingPageTemplate[] | undefined>(() => {
    if (isReviewSettingsError) return [];
    if (!reviewSettings) return undefined;
    const googleUrl = reviewSettings?.googleUrl;
    const facebookUrl = reviewSettings?.facebookUrl;
    return [
      googleUrl && {
        templateId: AffiliateReviewIds.GOOGLE,
        previewLink: googleUrl,
        htmlConfig: '',
        renderedHtml: '',
        type: AffiliateReviewType.GOOGLE,
        title: 'Google Review Link',
      },
      facebookUrl && {
        templateId: AffiliateReviewIds.FACEBOOK,
        previewLink: facebookUrl,
        htmlConfig: '',
        renderedHtml: '',
        type: AffiliateReviewType.FACEBOOK,
        title: 'Facebook Review Link',
      },
    ].filter((data) => !!data);
  }, [JSON.stringify(landingPageTemplates), reviewSettings, isReviewSettingsError]);

  return {
    landingPageReviewTemplates,
    affiliateReviewTemplates,
    isLoading: isListLandingPageTemplatesLoading || isReviewSettingsLoading,
    isError: isListLandingPageTemplatesError && isReviewSettingsError,
    isReviewSettingsError,
  };
};

export const useDeleteLandingPageTemplate = ({
  handleErrorResponse,
  handleSuccessResponse,
  groupIds,
}: ResponseCallbacks & {
  groupIds?: string[];
}) => {
  const queryClient = useQueryClient();

  const deleteLandingPageTemplateQuery = useDeleteLandingPageTemplateMutation({
    options: {
      onError: handleErrorResponse,
      onSuccess: () => {
        // Invalidate listLandingPageTemplates query to refetch after delete.
        queryClient.invalidateQueries({
          queryKey: getListLandingPageTemplatesQueryKey({ groupIds }),
        });
        handleSuccessResponse?.();
      },
    },
  });

  return deleteLandingPageTemplateQuery;
};

export const useGetReviewsInvitationTemplates = ({
  selectedLocationIds,
  handleErrorResponse,
  enabled,
}: useGetReviewsInvitationTemplatesProps) => {
  const getReviewsInvitationTemplates = ReviewsInvitationV1.Queries.useListInvitationTemplatesQuery(
    {
      groupIds: selectedLocationIds,
    },
    {
      select: (data) => data.templates,
      enabled,
      onError: handleErrorResponse,
      cacheTime: 0,
      staleTime: 0,
      retry: 2,
    }
  );

  return getReviewsInvitationTemplates;
};
